/**
 * DeveloperInlineFooter - Shared inline footer component for developer pages
 *
 * Displays:
 * - Version information (ECGridOS and Portal)
 * - Separator line from @ecgrid/react-components
 * - Links to Terms, Privacy Policy, and Change Log
 *
 * Simple text-based styling with responsive design
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { NS } from '@/i18n/namespaces';
import { Separator } from '@/components/ui';

export interface DeveloperInlineFooterProps {
  className?: string;
}

export const DeveloperInlineFooter: React.FC<DeveloperInlineFooterProps> = ({ className = '' }) => {
  const { t } = useTranslation(NS.COMMON);

  return (
    <div className={`mt-8 ${className}`}>
      <Separator className="mb-4" />

      <div className="text-center text-xs text-gray-600 space-y-2">
        {/* Version Information */}
        <p className="font-medium">
          {t('DeveloperInlineFooter_Version', 'ECGridOS v4.1 (Build 485) | Portal v1.6 (Build 6.8)')}
        </p>

        {/* Links */}
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-2">
          <a
            href="/terms"
            className="text-blue-600 hover:text-blue-800 hover:underline transition-colors"
          >
            {t('DeveloperInlineFooter_Terms', 'Terms of Service')}
          </a>

          <span className="text-gray-400 hidden sm:inline">•</span>

          <a
            href="https://www.ld.com/privacy/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 hover:underline transition-colors"
          >
            {t('DeveloperInlineFooter_Privacy', 'Privacy Policy')}
          </a>

          <span className="text-gray-400 hidden sm:inline">•</span>

          <a
            href="/change-log"
            className="text-blue-600 hover:text-blue-800 hover:underline transition-colors"
          >
            {t('DeveloperInlineFooter_ChangeLog', 'Change Log')}
          </a>
        </div>

        {/* Copyright */}
        <p className="text-gray-500 mt-3">
          {t('DeveloperInlineFooter_Copyright', '© 2015-{{year}} Loren Data Corp. All Rights Reserved.', { year: new Date().getFullYear() })}
        </p>
      </div>
    </div>
  );
};

DeveloperInlineFooter.displayName = 'DeveloperInlineFooter';
